import { defineStore } from 'pinia';
import { useKeycloakStore } from './keycloak.js';

const path = import.meta.env.VITE_APP_URL;

export const useFeedStore = defineStore('feed', {
  state: () => ({
    items: [],
    page: 0,
    hasMore: true,
    loading: false,
    activeFilter: 'all',
  }),

  actions: {
    async loadMain(userId) {
      if (this.loading || !this.hasMore) return;
      this.loading = true;
      try {
        const { default: axios } = await import('axios');
        const params = { page: this.page, size: 20 };
        if (userId) params.userId = userId;
        const { data } = await axios({
          url: `${path}feed/main`,
          method: 'GET',
          params,
          timeout: 15000,
          headers: { Authorization: `Bearer ${useKeycloakStore().token}` },
        });
        this.items.push(...(data.items || data));
        this.hasMore = data.hasMore ?? false;
        this.page++;
      } catch (err) {
        console.log('feed loadMain error', err);
      } finally {
        this.loading = false;
      }
    },

    async loadGroup(groupId, userId) {
      if (this.loading || !this.hasMore) return;
      this.loading = true;
      try {
        const { default: axios } = await import('axios');
        const { data } = await axios({
          url: `${path}feed/groups/${groupId}`,
          method: 'GET',
          params: { requestingUserId: userId, page: this.page, size: 20 },
          timeout: 15000,
          headers: { Authorization: `Bearer ${useKeycloakStore().token}` },
        });
        this.items.push(...(data.items || data));
        this.hasMore = data.hasMore ?? false;
        this.page++;
      } catch (err) {
        console.log('feed loadGroup error', err);
      } finally {
        this.loading = false;
      }
    },

    loadMockData(mockItems) {
      this.items = [...mockItems];
      this.hasMore = false;
    },

    async reactToItem({ itemId, reactionType, userId, authorName, authorAvatar }) {
      const item = this.items.find((i) => i.id === itemId);
      if (!item) return;
      if (!Array.isArray(item.reactions)) item.reactions = [];

      const had = item.reactions.find((r) => (r.users || []).includes(userId));
      const hadType = had ? had.reactionType : null;

      item.reactions.forEach((r) => {
        const userIdx = (r.users || []).indexOf(userId);
        if (userIdx > -1) {
          r.users.splice(userIdx, 1);
          r.count = Math.max(0, (r.count || 1) - 1);
        }
      });
      item.reactions = item.reactions.filter((r) => (r.count || 0) > 0);

      if (reactionType && reactionType !== hadType) {
        const existing = item.reactions.find((r) => r.reactionType === reactionType);
        if (existing) {
          existing.users.push(userId);
          existing.count = (existing.count || 0) + 1;
        } else {
          item.reactions.push({
            reactionType,
            count: 1,
            users: [userId],
            authors: [{ name: authorName, avatar: authorAvatar }],
          });
        }
      }

      try {
        const { default: axios } = await import('axios');
        await axios({
          url: `${path}post/react`,
          method: 'PUT',
          data: { reactionType, itemId },
          headers: { Authorization: `Bearer ${useKeycloakStore().token}` },
        });
      } catch (err) {
        console.log('feed react error', err);
      }
    },

    async reactToComment({ postId, commentId, replyId, reactionType, userId, authorName, authorAvatar }) {
      const item = this.items.find((i) => i.id === postId);
      if (!item || !item.comments) return;
      const comment = item.comments.find((c) => c.id === commentId);
      const target = replyId ? comment?.replies?.find((r) => r.id === replyId) : comment;
      if (!target) return;
      if (!Array.isArray(target.reactions)) target.reactions = [];

      const had = target.reactions.find((r) => (r.users || []).includes(userId));
      const hadType = had ? had.reactionType : null;

      target.reactions.forEach((r) => {
        const userIdx = (r.users || []).indexOf(userId);
        if (userIdx > -1) {
          r.users.splice(userIdx, 1);
          r.count = Math.max(0, (r.count || 1) - 1);
        }
      });
      target.reactions = target.reactions.filter((r) => (r.count || 0) > 0);

      if (reactionType && reactionType !== hadType) {
        const existing = target.reactions.find((r) => r.reactionType === reactionType);
        if (existing) {
          existing.users.push(userId);
          existing.count = (existing.count || 0) + 1;
        } else {
          target.reactions.push({
            reactionType,
            count: 1,
            users: [userId],
            authors: [{ name: authorName, avatar: authorAvatar }],
          });
        }
      }

      target.userLiked = !!(target.reactions.find((r) => r.reactionType === 'LIKE' && (r.users || []).includes(userId)));
      target.likesCount = target.reactions.reduce(
        (sum, r) => sum + (r.reactionType === 'LIKE' ? (r.count || 0) : 0),
        0
      );

      try {
        const { default: axios } = await import('axios');
        await axios({
          url: `${path}post/react`,
          method: 'PUT',
          data: { reactionType, itemId: target.id, itemType: replyId ? 'sub_comment' : 'comment' },
          headers: { Authorization: `Bearer ${useKeycloakStore().token}` },
        });
      } catch (err) {
        console.log('feed react-comment error', err);
      }
    },

    async addComment({ postId, comment }) {
      const item = this.items.find((i) => i.id === postId);
      if (!item) return;
      if (!item.comments) item.comments = [];
      item.comments.push(comment);
      if (item.commentsCount !== undefined) item.commentsCount++;

      try {
        const { default: axios } = await import('axios');
        await axios({
          url: `${path}post/comment`,
          method: 'POST',
          data: comment,
          params: { postId },
          headers: { Authorization: `Bearer ${useKeycloakStore().token}` },
        });
      } catch (err) {
        console.log('feed comment error', err);
      }
    },

    async addReply({ postId, commentId, reply }) {
      const item = this.items.find((i) => i.id === postId);
      if (!item) return;
      const comment = item.comments?.find((c) => c.id === commentId);
      if (comment) {
        if (!comment.replies) comment.replies = [];
        comment.replies.push(reply);
      }

      try {
        const { default: axios } = await import('axios');
        await axios({
          url: `${path}post/sub_comment`,
          method: 'POST',
          data: reply,
          params: { commentId },
          headers: { Authorization: `Bearer ${useKeycloakStore().token}` },
        });
      } catch (err) {
        console.log('feed reply error', err);
      }
    },

    async createPost(postData) {
      try {
        const { default: axios } = await import('axios');
        const { data } = await axios({
          url: `${path}post/create`,
          method: 'POST',
          data: postData,
          headers: { Authorization: `Bearer ${useKeycloakStore().token}` },
        });
        this.items.unshift(data);
        return data;
      } catch (err) {
        console.log('feed createPost error', err);
      }
    },

    async repostItem({ postId, userId, authorName, authorAvatar }) {
      const item = this.items.find((i) => i.id === postId);
      if (!item) return;
      if (!item.reactions) item.reactions = [];
      const repostReaction = item.reactions.find((r) => r.reactionType === 'Repost');
      if (repostReaction) {
        repostReaction.count++;
        repostReaction.users.push(userId);
      } else {
        item.reactions.push({
          reactionType: 'Repost',
          count: 1,
          users: [userId],
          authors: [{ name: authorName, avatar: authorAvatar }],
        });
      }

      try {
        const { default: axios } = await import('axios');
        await axios({
          url: `${path}post/repost`,
          method: 'POST',
          params: { postId },
          data: { author: { userId, name: authorName, avatar: authorAvatar } },
          headers: { Authorization: `Bearer ${useKeycloakStore().token}` },
        });
      } catch (err) {
        console.log('feed repost error', err);
      }
    },

    addRepostPost({ item, text, userId, authorName, authorAvatar }) {
      const repost = {
        id: `repost-${Date.now()}`,
        type: 'REPOST',
        authorName,
        author: { userId, name: authorName, avatar: authorAvatar },
        text: text || '',
        timeAgo: "à l'instant",
        originalPost: item,
        reactions: [],
        comments: [],
        commentsCount: 0,
      };
      this.items.unshift(repost);
      return repost;
    },

    async votePoll({ pollId, optionId, userId }) {
      const item = this.items.find((i) => i.poll?.id === pollId);
      if (!item || !item.poll) return;
      const option = item.poll.options.find((o) => o.id === optionId);
      if (option) {
        option.votes++;
        item.poll.totalVotes++;
        item.poll.userVote = optionId;
      }

      try {
        const { default: axios } = await import('axios');
        await axios({
          url: `${path}polls/${pollId}/responses`,
          method: 'POST',
          data: { optionId, userId },
          headers: { Authorization: `Bearer ${useKeycloakStore().token}` },
        });
      } catch (err) {
        console.log('feed votePoll error', err);
      }
    },

    reset() {
      this.items = [];
      this.page = 0;
      this.hasMore = true;
      this.loading = false;
    },

    setFilter(filter) {
      this.activeFilter = filter;
    },
  },

  getters: {
    filteredItems: (state) => {
      if (state.activeFilter === 'all') return state.items;
      return state.items.filter((item) => {
        if (state.activeFilter === 'posts') return item.type === 'POST';
        if (state.activeFilter === 'polls') return item.type === 'POLL' || item.type === 'SPONSORED_POLL';
        if (state.activeFilter === 'sponsored') return item.type === 'SPONSORED_POST' || item.type === 'SPONSORED_POLL';
        return true;
      });
    },
  },
});
